<!-- Content -->
<div *ngIf="userForm; else loading" class="pt-4">
  <form [formGroup]="userForm" (ngSubmit)="save()">

    <!-- Avatar Section -->
    <div class="mb-6">
      <div class="flex items-center gap-5 justify-center">
        <div class="relative group cursor-pointer" (click)="fileInput.click()">
          <div class="w-[100px] h-[100px] rounded-full overflow-hidden border border-gray-200 bg-gray-50">
            <img [src]="previewImage || 'assets/images/user-profile.png'" alt="Profile"
              class="w-full h-full object-cover"
              onerror="this.src='assets/images/user-profile.png'; this.onerror=null;">

            <!-- Hover Overlay -->
            <div
              class="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <svg class="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
          </div>
          <input #fileInput type="file" hidden accept="image/*" (change)="onFileSelected($event)">
        </div>
      </div>
    </div>

    <!-- Form Fields -->
    <div class="space-y-4">

      <!-- Name -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          {{ 'dashboard.profile.name' | translate }} <span class="text-red-500">*</span>
        </label>
        <input type="text" formControlName="name"
          class="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
          [class.border-red-300]="isFieldInvalid('name')">
      </div>

      <!-- Email (Read Only) -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          {{ 'dashboard.profile.email' | translate }}
        </label>
        <input type="email" formControlName="email" readonly
          class="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-500 cursor-not-allowed">
      </div>

      <!-- Password -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          {{ 'dashboard.profile.password' | translate }} <span class="text-red-500">*</span>
        </label>
        <div class="relative">
          <input [type]="showPassword ? 'text' : 'password'" formControlName="password" placeholder="******"
            class="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all pr-10">
          <button type="button" (click)="showPassword = !showPassword"
            class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <svg *ngIf="!showPassword" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <svg *ngIf="showPassword" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a10.05 10.05 0 011.574-2.59M5.22 5.22a3 3 0 004.158 4.158m6.064-1.216A6.978 6.978 0 0118.571 8a10.05 10.05 0 011.972 4c-1.274 4.057-5.064 7-9.542 7-1.456 0-2.846-.3-4.11-.854M3 3l18 18" />
            </svg>
          </button>
        </div>
        <p class="text-xs text-gray-500 mt-1">
          {{ 'dashboard.profile.password_hint' | translate }}
        </p>
        <app-password-checklist *ngIf="userForm.get('password')?.value" [value]="userForm.get('password')?.value"
          [touched]="!!userForm.get('password')?.touched"></app-password-checklist>
      </div>

      <!-- Confirm password -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          {{ 'confirm_password' | translate }}
        </label>
        <div class="relative">
          <input [type]="showConfirmPassword ? 'text' : 'password'" formControlName="confirmPassword" placeholder="******"
            class="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all pr-10"
            [class.border-red-300]="userForm.hasError('mismatch') && userForm.get('confirmPassword')?.touched">
          <button type="button" (click)="showConfirmPassword = !showConfirmPassword"
            class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <svg *ngIf="!showConfirmPassword" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <svg *ngIf="showConfirmPassword" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a10.05 10.05 0 011.574-2.59M5.22 5.22a3 3 0 004.158 4.158m6.064-1.216A6.978 6.978 0 0118.571 8a10.05 10.05 0 011.972 4c-1.274 4.057-5.064 7-9.542 7-1.456 0-2.846-.3-4.11-.854M3 3l18 18" />
            </svg>
          </button>
        </div>
        <p *ngIf="userForm.hasError('mismatch') && userForm.get('confirmPassword')?.touched"
          class="text-xs text-red-500 mt-1">{{ 'passwords_do_not_match' | translate }}</p>
      </div>

    </div>

    <!-- Footer -->
    <div class="mt-8 flex justify-end gap-3">
      <app-button [label]="'common.cancel' | translate" variant="outline" (onClick)="close()"></app-button>

      <app-button [label]="'common.save' | translate" [loading]="saving" (onClick)="save()"
        [disabled]="userForm.invalid || (userForm.pristine && !selectedFile)"></app-button>
    </div>

  </form>
</div>

<ng-template #loading>
  <div class="flex items-center justify-center py-12">
    <div class="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
  </div>
</ng-template>